import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams, useMatch } from "react-router-dom"
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input'
import { validateAddEditForm } from './validations';
import CustomerDetails from "./components/CustomerDetails";
import Header from './components/Header';
import Buttons from './components/Buttons';
import Tabs from "./components/Tabs";
import PropertiesTab from './components/PropertiesTab';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES, DEFAULTS, GENERIC_LOOKUP_KEYS, MaxLength } from "../../../../utils/appConstant"
import { Validation } from '../../../../interfaces/common';
import { AddEdit as AddEditInterface, AddEditApi } from '../../../../interfaces/customer.interface';
import CustomerService from '../../../../services/customer.service';
import CommonService from '../../../../services/common.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import useFetch from '../../../../hooks/useFetch';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import { STATUS_CODE } from '../../../../utils/apiConstant';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import ConfirmationFormRouting from '../../../../components/ConfirmationFormRouting';
import Modal from '../../../../components/Modal';
import Button from "../../../../components/Button";

const initialFormData: AddEditInterface = {
  customerName: '',
  primarySalutation: '',
  primaryFirstName: '',
  primaryLastName: '',
  primaryEmail: '',
  secondaryEmail: '',
  countryCode: '',
  primaryPhone: '',
  emergencyCountryCode: '',
  emergencyPhone: '',
  displayName: '',
  companyName: '',
  website: '',
  hstNo: '',
  status: true,
  notes: '',
  billingCurrency: DEFAULTS.currency,
  billingAddress1: '',
  billingAddress2: '',
  billingCountry: DEFAULTS.country,
  billingState: DEFAULTS.state,
  billingCity: '',
  billingPostal: '',
}

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  billingCountry: '',
  billingState: '',
  billingCity: '',
  primarySalutation: ''
}

const AddEditView: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.resources + AuthRoutes.customers + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.resources + AuthRoutes.customers + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate()

  const [tab, setTab] = useState<'customerDetails' | 'properties'>('customerDetails');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const currentIdRef = useRef<string | undefined>(id);

  const [shouldBlock, setShouldBlock] = useState(!view);

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  useEffect(() => {
    setTab(location?.state?.tab ?? 'customerDetails');
  }, [location.state, location.key]);

  const { data: properties } = useFetch({
    service: CustomerService,
    func: 'customerProperties',
    payload: {
      id: id,
      page: 1,
      perPage: MaxLength.pageSize,
    },
    dependency: [id],
    skip: !id
  });

  const { data: allCountries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: '' },
    dependency: [],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: countries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: dropdownSearchData.billingCountry },
    dependency: [dropdownSearchData.billingCountry]
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: dropdownSearchData.billingState },
    dependency: [dropdownSearchData.billingState]
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '', stateId: formData.billingState },
    dependency: [formData.billingState]
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.billingCity, stateId: formData.billingState },
    dependency: [dropdownSearchData.billingCity, formData.billingState]
  });

  const { data: allSalutations } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.salutation, searchText: '' },
    dependency: []
  });

  const { data: salutations } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.salutation, searchText: dropdownSearchData.primarySalutation },
    dependency: [dropdownSearchData.primarySalutation]
  });

  const { data: allBillingCurrency } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.billingCurrency, searchText: '' },
    dependency: [],
  });

  const { data: billingCurrency } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.billingCurrency, searchText: dropdownSearchData.billingCurrency },
    dependency: [dropdownSearchData.billingCurrency]
  });

  useEffect(() => {
    currentIdRef.current = id;
    setFormData(initialFormData)
    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
    setShouldBlock(!view);
  }, [location.key, id, view]);

  const { loading: getContentLoading } = useFetch({
    service: CustomerService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: (response: any) => {
      if (!currentIdRef.current) return;

      setFormData({
        customerName: response.customer_name || '',
        primarySalutation: response.primary_salutation || '',
        primaryFirstName: response.primary_first_name || '',
        primaryLastName: response.primary_last_name || '',
        primaryEmail: response.primary_email || '',
        secondaryEmail: response.secondary_email || '',
        countryCode: response.primary_country_code || '',
        primaryPhone: response.primary_phone || '',
        emergencyCountryCode: response.emergency_country_code || '',
        emergencyPhone: response.emergency_phone || '',
        displayName: response.display_name || '',
        companyName: response.company_name || '',
        website: response.website || '',
        hstNo: response.hst_no || '',
        status: response.is_active !== undefined ? response.is_active : true,
        notes: response.notes || '',
        billingCurrency: response.billing_currency || '',
        billingAddress1: response.billing_address1 || '',
        billingAddress2: response.billing_address2 || '',
        billingCountry: response.billing_country || '',
        billingState: response.billing_state || '',
        billingCity: response.billing_city || '',
        billingPostal: response.billing_postal || '',
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.resources + AuthRoutes.customers);
    }
  });

  const handleTabChange = useCallback((e: any, value: 'customerDetails' | 'properties') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.primaryPhone) {
      const phoneNumber = parsePhoneNumber(event?.target?.value ?? "")

      setFormData((prev) => {
        return {
          ...prev,
          [VALIDATION_FIELD_MAPPING.primaryPhone]: phoneNumber && phoneNumber.nationalNumber,
          [VALIDATION_FIELD_MAPPING.countryCode]: phoneNumber ? `+${phoneNumber && phoneNumber.countryCallingCode}` : ""
        };
      });
    } else if (name === VALIDATION_FIELD_MAPPING.billingState) {
      setFormData((prev) => {
        return {
          ...prev,
          [VALIDATION_FIELD_MAPPING.billingCity]: '',
          [name]: event.target.value
        };
      });
    } else {
      let value: any;

      if (name === VALIDATION_FIELD_MAPPING.status) {
        value = event.target.checked
      } else {
        value = event.target.value
      }

      setFormData((prev) => {
        return {
          ...prev,
          [name]: value
        };
      });
    }
  }, [setFormData]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handleInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateAddEditForm({
      ...formData,
      [name]: event.target.value.trim(),
    }, name);

    setValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });

    if (!id && !view && name === VALIDATION_FIELD_MAPPING.customerName && !formData.displayName) {
      setFormData((prev) => {
        return {
          ...prev,
          [VALIDATION_FIELD_MAPPING.customerName]: event.target.value,
          [VALIDATION_FIELD_MAPPING.displayName]: event.target.value
        };
      });
    }
  }, [setFormData, id, view, formData]);

  const handleSubmit = useCallback(async (action: '1' | '2', event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateAddEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (action === '1') setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload: AddEditApi = {
        customer_name: formData.customerName.trim(),
        primary_salutation: formData.primarySalutation,
        primary_first_name: formData.primaryFirstName.trim(),
        primary_last_name: formData.primaryLastName.trim(),
        primary_email: formData.primaryEmail.trim(),
        secondary_email: formData.secondaryEmail?.trim(),
        primary_country_code: formData.countryCode,
        primary_phone: formData.primaryPhone?.trim(),
        emergency_country_code: formData.emergencyCountryCode,
        emergency_phone: formData.emergencyPhone?.trim(),
        display_name: formData.displayName.trim(),
        company_name: formData.companyName?.trim(),
        website: formData.website?.trim(),
        hst_no: formData.hstNo?.trim(),
        is_active: formData.status,
        notes: formData.notes?.trim(),
        billing_currency: formData.billingCurrency,
        billing_address1: formData.billingAddress1.trim(),
        billing_address2: formData.billingAddress2?.trim(),
        billing_country: formData.billingCountry,
        billing_state: formData.billingState,
        billing_city: formData.billingCity,
        billing_postal: formData.billingPostal.trim(),
      }

      let response: any
      if (!edit) {
        response = await CustomerService.add(payload);
      } else if (id) {
        response = await CustomerService.edit(id, payload);
      }

      if (response.success !== false) {
        toast.success(<Toast message={id ? APP_SUCCESS_MESSAGES.customerUpdated : APP_SUCCESS_MESSAGES.customerCreated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.customerName);
            input && input.focus();
            navigate(Modules.resources + AuthRoutes.customers + RouteParamsTypes.add, { replace: true });
          } else {
            navigate(Modules.resources + AuthRoutes.customers);
          }
        }, 0);
      }

      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    } catch (err: any) {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, edit, formData, navigate]);

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name === VALIDATION_FIELD_MAPPING.notes) return;
    if (e.key === 'Enter') handleSubmit('1');
  }, [handleSubmit]);

  const handleCancel = () => {
    setShouldBlock(false);
    setTimeout(() => {
      navigate(Modules.resources + AuthRoutes.customers);
    }, 0);
  }

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className='card-header text-center'>
        <Spinner />
      </div>
    )
  } else if (tab === 'customerDetails') {
    formContent = (
      <CustomerDetails
        view={view}
        formData={formData}
        validation={validation}
        dropdownSearchData={dropdownSearchData}
        salutations={salutations}
        allSalutations={allSalutations}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        countries={countries}
        states={states}
        allCountries={allCountries}
        allStates={allStates}
        cities={cities}
        allCities={allCities}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        billingCurrency={billingCurrency}
        allBillingCurrency={allBillingCurrency}
      />
    )
  } else if (tab === 'properties') {
    formContent = (
      <PropertiesTab
        properties={properties}
      />
    )
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} loading={getContentLoading} />

      <div className="card mb-6">
        <h5 className="card-header">{view ? t("customers.addEdit.pageTexts.title.viewTitle") : (!id ? t("customers.addEdit.pageTexts.title.addTitle") : t("customers.addEdit.pageTexts.title.editTitle"))}</h5>

        <hr className="m-0"></hr>

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </>
  );
};

export default AddEditView;
